import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/auth';
import { ControllerTextField } from '../../components/Inputs/Textfield';
import Button from '../../components/Button';
import { login as loginValidation } from '../../validation/schemas';
import * as T from '../../components/Typography';
import colors from '../../theme/colors';
import { routes, external } from '../../constants';
import { breakpoints } from '../../theme';
import { sanitiseInput } from '../../helpers/sanitise-input';
import Notification from '../../components/Notification';

interface LoginFormInputs {
  email: string;
  password: string;
}

const LoadingScreen: React.FC = () => (
  <Grid container flexDirection="column" alignItems="center">
    <Grid item mobile={12} tablet={11} alignItems="center">
      <T.H5 ta="center" color={colors.neutral.shades.shade700}>
        Loading your portal...
      </T.H5>
      <T.TextSmallRegular
        ta="center"
        mb={5}
        center
        color={colors.neutral.shades.shade600}
      >
        Please wait a moment - we're getting things ready for you. You'll be
        redirected shortly.
      </T.TextSmallRegular>
    </Grid>
  </Grid>
);

const Login: React.FC = () => {
  const { handleSubmit, control } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginValidation)
  });

  const { login, loading, error, user } = useAuth();
  const navigate = useNavigate();
  const isWideScreen = useMediaQuery(`(min-width:${breakpoints.tablet}px)`);

  useEffect(() => {
    if (user) {
      navigate(routes.HOME);
    }
  }, [user, navigate]);

  const Form = () => (
    <form
      onSubmit={handleSubmit(async (data) => {
        await login({ ...data, email: sanitiseInput(data?.email) });
        navigate(routes.HOME);
      })}
      style={{ width: '100%', maxWidth: '384px' }}
    >
      <Box>
        <Box>
          <ControllerTextField
            control={control}
            label="Email"
            name="email"
            placeholder="Email"
            type="email"
          />
        </Box>

        <Box textAlign="right">
          <ControllerTextField
            control={control}
            label="Password"
            name="password"
            placeholder="Password"
            type="password"
          />
          <T.Link
            to={routes.RESET_PASSWORD}
            color={colors.blue.shades.shade700}
            decoration="none"
          >
            Forgot your password?
          </T.Link>
        </Box>
        <Box mt={2}>
          <Button type="submit" variant="blue">
            Log in
          </Button>
          <Box>
            <T.TextSmallRegular
              ta="center"
              mt="4"
              color={colors.neutral.shades.shade500}
            >
              Don't have an account?{' '}
              <T.Link
                to={external.signUpUrl}
                external
                color={colors.blue.shades.shade700}
                decoration="none"
              >
                Sign up now
              </T.Link>
            </T.TextSmallRegular>
          </Box>
        </Box>
      </Box>
    </form>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <T.H4
        mt={!isWideScreen ? 6 : 0}
        ta="center"
        color={colors.neutral.shades.shade700}
      >
        WELCOME BACK!
      </T.H4>
      <T.TextSmallRegular
        ta="center"
        mb={4}
        mt={2}
        color={colors.neutral.shades.shade600}
      >
        Enter your email and password to login to access your portal.
      </T.TextSmallRegular>
      <Form />
      {error && <Notification type="error" message={error} />}
    </Grid>
  );
};

export default Login;
